/**
 * Web-side wiring for `NetworkMonitor`:
 *  - Seeds core with the browser's initial online state.
 *  - Re-runs a `/health` ping shortly after boot to catch VPN /
 *    captive-portal scenarios where `navigator.onLine` lies.
 *  - Subscribes to `online` / `offline` window events through the
 *    `webNetwork` adapter, with `online` triggering a fresh ping
 *    instead of trusting the event blindly.
 *
 * The actual React hook (`useNetworkStatus`) is now shared with
 * mobile in `@splitfiesta/core` — re-exported below so existing
 * imports (`@/hooks/useNetworkStatus`) keep working.
 */
import {
  setActualOnlineStatus,
  setInitialOnlineStatus,
} from '@splitfiesta/core';
import { webNetwork } from '@splitfiesta/platform-web';

// Seed core's NetworkMonitor synchronously at module load so consumers
// reading `checkIsOnline()` before the first React render get the
// browser's truth, not the optimistic default.
setInitialOnlineStatus(webNetwork.isOnline());

let hasCheckedInitialConnectivity = false;
const HEALTH_URL = `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/health`;

async function checkConnectivityOnStartup() {
  if (typeof window === 'undefined' || hasCheckedInitialConnectivity) return;
  hasCheckedInitialConnectivity = true;

  if (!webNetwork.isOnline()) {
    setActualOnlineStatus(false);
    return;
  }

  const ok = await webNetwork.ping?.(HEALTH_URL, 3000);
  if (ok === false) {
    setActualOnlineStatus(false);
  }
}

if (typeof window !== 'undefined') {
  setTimeout(checkConnectivityOnStartup, 100);
}

// Bridge browser online/offline events into core. Done at module load
// (one subscription per app process) rather than inside the hook so
// every consumer of `useNetworkStatus` shares the same subscription.
if (typeof window !== 'undefined') {
  webNetwork.subscribe(online => {
    if (online) checkActualConnectivity();
    else setActualOnlineStatus(false);
  });
}

/**
 * Core re-exports kept here so existing callers
 * (`import { setActualOnlineStatus } from '@/hooks/useNetworkStatus'`) keep
 * working without a sweep.
 */
export {
  setActualOnlineStatus,
  setOnlineManagerCallback,
  markOffline,
  markOnline,
  checkIsOnline,
  useNetworkStatus,
} from '@splitfiesta/core';

/**
 * Force a `/health` ping and propagate the result through core. Used
 * by the browser `online` event handler and by tests that want to
 * re-check after a manual network state change.
 */
export async function checkActualConnectivity(): Promise<boolean> {
  const ok = (await webNetwork.ping?.(HEALTH_URL, 5000)) ?? false;
  setActualOnlineStatus(ok);
  return ok;
}
